import {useCallback, useEffect, useMemo} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {MobileViewerSeekbar} from '../../../../../../design-system/organisms/MobileViewerSeekbar/MobileViewerSeekbar';
import {PcViewerSeekbar} from '../../../../../../design-system/organisms/PcViewerSeekbar/PcViewerSeekbar';
import {ViewerType} from '../constant/viewerConstant';
import {
  goToSubTotalPage,
  setIsHandlingSeekBar,
  setIsProgressBarExpanded,
  setLoadedProgress,
} from '../state/textViewerSlice';
import {TextViewerReadType} from '../type/TextViewerReadType';
import {calcPageByProgress} from '../util/calcPageByProgress';
import {getPageSubtotalCount} from '../util/getPageSubtotalCount';

export function TextViewerSeekbarContainer({productId}: {productId: number}) {
  const {isMobile} = requestContext.requestInfo.display;

  const currentPage = useTypedSelector(
    state => state.textViewer.viewData[productId]?.currentPage || 1
  );
  const currentChapterId = useTypedSelector(
    state => state.textViewer.viewData[productId]?.currentChapterId || 0
  );
  const htmlInfoList = useTypedSelector(
    state => state.textViewer.viewData[productId]?.htmlInfoList || []
  );
  const chapterCount = useTypedSelector(
    state => state.textViewer.viewData[productId]?.chapterCount || 0
  );
  const chapterPageCountList = useTypedSelector(
    state => state.textViewer.viewData[productId]?.chapterPageCountList
  );
  const isViewerEnd = useTypedSelector(
    state => state.textViewer.viewData[productId]?.isViewerEnd || false
  );
  const isTwoPageLayout = useTypedSelector(({textViewer: {settings}}) =>
    isMobile || settings.readType === TextViewerReadType.Scroll
      ? false
      : settings.isTwoPageLayout
  );
  const pageSubtotalCount = useMemo(
    () =>
      getPageSubtotalCount({
        currentPage,
        currentChapterId,
        isViewerEnd,
        chapterPageCountList,
      }),
    [chapterPageCountList, currentChapterId, currentPage, isViewerEnd]
  );
  const pageTotalCount = useMemo(
    () =>
      chapterPageCountList?.reduce((prev, current) => prev + current, 0) || 0,
    [chapterPageCountList]
  );

  const dispatch = useTypedDispatch();

  useEffect(() => {
    if (!chapterCount) {
      return;
    }
    dispatch(
      setLoadedProgress({
        productId,
        progress: htmlInfoList.length / chapterCount,
      })
    );
  }, [chapterCount, dispatch, htmlInfoList.length, productId]);

  const isProgressBarExpanded = useTypedSelector(
    state =>
      state.textViewer.viewData[productId]?.isProgressBarExpanded || false
  );

  const loadedProgress = useTypedSelector(
    state => state.textViewer.viewData[productId]?.loadedProgress || 0
  );

  const onMouseEnter = useCallback(() => {
    dispatch(
      setIsProgressBarExpanded({productId, isProgressBarExpanded: true})
    );
  }, [dispatch, productId]);

  const onMouseLeave = useCallback(() => {
    dispatch(
      setIsProgressBarExpanded({productId, isProgressBarExpanded: false})
    );
  }, [dispatch, productId]);

  const onPressProgressBar = useCallback(() => {
    dispatch(setIsHandlingSeekBar({productId, isHandling: true}));
    dispatch(
      setIsProgressBarExpanded({productId, isProgressBarExpanded: true})
    );
  }, [dispatch, productId]);

  // 인디케이터 누른 상태로 이동중에는 실제 페이지를 변경하지 않음
  const onChangeProgress = useCallback(
    (newProgress: number) => {
      dispatch(setIsHandlingSeekBar({productId, isHandling: true}));
      const newSubTotalPage = calcPageByProgress({
        newProgress,
        totalPage: pageTotalCount,
        isTwoPageLayout,
      });
      dispatch(goToSubTotalPage({productId, subTotalPage: newSubTotalPage}));
    },
    [dispatch, isTwoPageLayout, pageTotalCount, productId]
  );

  const onReleaseProgressBarMobile = useCallback(() => {
    dispatch(setIsHandlingSeekBar({productId, isHandling: false}));
  }, [dispatch, productId]);

  // PC의 경우 addEventListener로 mouseDown시 함수를 넣어주므로 up 시에는 progress를 재계산 해야합니다
  const onReleaseProgressBarPc = useCallback(
    (newProgress: number) => {
      const newSubTotalPage = calcPageByProgress({
        newProgress,
        totalPage: pageTotalCount,
        isTwoPageLayout,
      });
      dispatch(goToSubTotalPage({productId, subTotalPage: newSubTotalPage}));
      dispatch(setIsHandlingSeekBar({productId, isHandling: false}));
    },
    [dispatch, isTwoPageLayout, pageTotalCount, productId]
  );

  return isMobile ? (
    <MobileViewerSeekbar
      viewerType={ViewerType.TextViewer}
      loadedProgress={loadedProgress}
      currentProgress={(pageSubtotalCount - 1) / (pageTotalCount - 1)}
      totalPage={pageTotalCount}
      currentPage={pageSubtotalCount}
      loadedPage={pageTotalCount}
      onPressProgressBar={onPressProgressBar}
      onChangeProgress={onChangeProgress}
      onReleaseProgressBar={onReleaseProgressBarMobile}
    />
  ) : (
    <PcViewerSeekbar
      loadedProgress={loadedProgress}
      currentProgress={(pageSubtotalCount - 1) / (pageTotalCount - 1)}
      totalPage={pageTotalCount}
      currentPage={pageSubtotalCount}
      isProgressBarExpanded={isProgressBarExpanded}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onPressProgressBar={onPressProgressBar}
      onChangeProgress={onChangeProgress}
      onReleaseProgressBar={onReleaseProgressBarPc}
    />
  );
}
